import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { ArrowLeft, CheckCircle, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';


const LIKERT_LABELS = {
  es: ['Muy en desacuerdo', 'En desacuerdo', 'Neutral', 'De acuerdo', 'Muy de acuerdo'],
  en: ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree']
};

const PulseSurveyResponse = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const texts = {
    es: {
      back: 'Volver al Dashboard',
      anonymous: 'Esta encuesta es anónima',
      anonymousDesc: 'Tus respuestas no serán vinculadas a tu identidad.',
      required: 'Requerida',
      submit: 'Enviar Respuestas',
      submitting: 'Enviando...',
      successTitle: 'Encuesta completada',
      successMsg: 'Tus respuestas han sido registradas exitosamente.',
      backToDashboard: 'Volver al Dashboard',
      alreadyResponded: 'Ya has respondido esta encuesta.',
      notFound: 'Encuesta no encontrada.',
      errorLoad: 'Error al cargar la encuesta.',
      errorSubmit: 'Error al enviar respuestas.',
      fillRequired: 'Por favor responde todas las preguntas requeridas.',
      questionOf: 'de',
      writeHere: 'Escribe tu respuesta aquí...'
    },
    en: {
      back: 'Back to Dashboard',
      anonymous: 'This survey is anonymous',
      anonymousDesc: 'Your responses will not be linked to your identity.',
      required: 'Required',
      submit: 'Submit Responses',
      submitting: 'Submitting...',
      successTitle: 'Survey completed',
      successMsg: 'Your responses have been recorded successfully.',
      backToDashboard: 'Back to Dashboard',
      alreadyResponded: 'You have already responded to this survey.',
      notFound: 'Survey not found.',
      errorLoad: 'Error loading survey.',
      errorSubmit: 'Error submitting responses.',
      fillRequired: 'Please answer all required questions.',
      questionOf: 'of',
      writeHere: 'Write your answer here...'
    }
  };
  const t = texts[language] || texts.en;

  useEffect(() => {
    loadSurvey();
  }, [surveyId, language]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/v5/pulso/employee/survey/${surveyId}?lang=${language}`);
      if (res.data.success) {
        const data = res.data.data;
        if (data.already_responded) {
          toast.info(t.alreadyResponded);
          navigate('/dashboard');
          return;
        }
        // Parse opciones if it's a JSON string
        data.preguntas = data.preguntas.map(q => ({
          ...q,
          opciones: typeof q.opciones === 'string' ? JSON.parse(q.opciones) : q.opciones
        }));
        setSurvey(data);
      }
    } catch (err) {
      toast.error(t.errorLoad);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLikertChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleTextChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (!survey) return;

    const requiredQuestions = survey.preguntas.filter(q => q.requerida);
    const unanswered = requiredQuestions.filter(q => responses[q.id] === undefined || responses[q.id] === '');
    if (unanswered.length > 0) {
      toast.error(t.fillRequired);
      return;
    }

    try {
      setSubmitting(true);
      const res = await axios.post(
        `/v5/pulso/employee/survey/${surveyId}/respond`,
        { respuestas: responses }
      );
      if (res.data.success) {
        setSubmitted(true);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || t.errorSubmit;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="pulse-survey-loading">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="pulse-survey-success">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="text-xl font-semibold text-gray-900">{t.successTitle}</h2>
            <p className="text-gray-600">{t.successMsg}</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="mt-4 bg-teal-600 hover:bg-teal-700"
              data-testid="pulse-survey-back-btn"
            >
              {t.backToDashboard}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!survey) return null;

  const answeredCount = Object.keys(responses).filter(k => responses[k] !== undefined && responses[k] !== '').length;
  const totalQuestions = survey.preguntas.length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pulse-survey-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-3 text-gray-600 hover:text-gray-900 -ml-2"
            data-testid="pulse-survey-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {t.back}
          </Button>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="pulse-survey-title">{survey.nombre}</h1>
          {survey.descripcion && <p className="text-gray-600 mt-1">{survey.descripcion}</p>}
          <div className="flex items-center gap-2 mt-3">
            <Badge variant="outline" className="text-xs">{survey.categoria}</Badge>
            {survey.anonima && (
              <Badge className="bg-green-100 text-green-700 text-xs">
                <Shield className="w-3 h-3 mr-1" /> {t.anonymous}
              </Badge>
            )}
          </div>
          {survey.anonima && (
            <p className="text-sm text-green-600 mt-2">{t.anonymousDesc}</p>
          )}
        </div>

        {/* Progress */}
        <div className="mb-4 text-sm text-gray-500">
          {answeredCount} {t.questionOf} {totalQuestions}
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
            <div
              className="bg-teal-500 h-1.5 rounded-full transition-all"
              style={{ width: `${totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {survey.preguntas.map((q, idx) => (
            <Card key={q.id} className="border" data-testid={`pulse-question-${idx}`}>
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-sm font-medium text-gray-800">
                    <span className="text-gray-400 mr-2">{idx + 1}.</span>
                    {q.texto}
                  </CardTitle>
                  {q.requerida && (
                    <Badge variant="outline" className="text-xs ml-2 shrink-0 text-red-500 border-red-200">
                      {t.required}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                {(q.tipo === 'LIKERT' || q.tipo === 'SCALE_1_5') && (
                  <div className="flex gap-2 flex-wrap" data-testid={`pulse-likert-${idx}`}>
                    {[1, 2, 3, 4, 5].map(val => (
                      <button
                        key={val}
                        onClick={() => handleLikertChange(q.id, val)}
                        className={`flex-1 min-w-[60px] py-2 px-1 rounded-lg border text-xs transition-all ${
                          responses[q.id] === val
                            ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                        }`}
                        data-testid={`pulse-likert-${idx}-val-${val}`}
                      >
                        <div className="font-semibold text-sm">{val}</div>
                        <div className="hidden sm:block mt-0.5 leading-tight">
                          {(LIKERT_LABELS[language] || LIKERT_LABELS.en)[val - 1]}
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {q.tipo === 'SCALE_1_10' && (
                  <div className="flex gap-1 flex-wrap" data-testid={`pulse-scale10-${idx}`}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(val => (
                      <button
                        key={val}
                        onClick={() => handleLikertChange(q.id, val)}
                        className={`w-9 h-9 rounded-lg border text-sm font-medium transition-all ${
                          responses[q.id] === val
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                        }`}
                        data-testid={`pulse-scale10-${idx}-val-${val}`}
                      >
                        {val}
                      </button>
                    ))}
                  </div>
                )}

                {q.tipo === 'TEXT' && (
                  <Textarea
                    placeholder={t.writeHere}
                    value={responses[q.id] || ''}
                    onChange={e => handleTextChange(q.id, e.target.value)}
                    className="resize-none"
                    rows={3}
                    data-testid={`pulse-text-${idx}`}
                  />
                )}

                {q.tipo === 'SINGLE_CHOICE' && q.opciones && (
                  <div className="space-y-2" data-testid={`pulse-single-${idx}`}>
                    {(Array.isArray(q.opciones) ? q.opciones : []).map((opt, oi) => (
                      <button
                        key={oi}
                        onClick={() => handleLikertChange(q.id, opt)}
                        className={`w-full text-left py-2 px-3 rounded-lg border text-sm transition-all ${
                          responses[q.id] === opt
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                        }`}
                        data-testid={`pulse-single-${idx}-opt-${oi}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}

                {q.tipo === 'EMOJI' && q.opciones && (
                  <div className="flex gap-3 justify-center" data-testid={`pulse-emoji-${idx}`}>
                    {(Array.isArray(q.opciones) ? q.opciones : []).map((emoji, ei) => (
                      <button
                        key={ei}
                        onClick={() => handleLikertChange(q.id, ei + 1)}
                        className={`text-3xl p-2 rounded-xl border-2 transition-all ${
                          responses[q.id] === ei + 1
                            ? 'border-teal-500 bg-teal-50 scale-110 shadow-md'
                            : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`pulse-emoji-${idx}-val-${ei + 1}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}

                {q.tipo === 'YES_NO' && (
                  <div className="flex gap-3" data-testid={`pulse-yesno-${idx}`}>
                    {[
                      { val: 1, label: language === 'es' ? 'Sí' : 'Yes' },
                      { val: 0, label: 'No' }
                    ].map(opt => (
                      <button
                        key={opt.val}
                        onClick={() => handleLikertChange(q.id, opt.val)}
                        className={`flex-1 py-3 px-4 rounded-lg border text-sm font-medium transition-all ${
                          responses[q.id] === opt.val
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300'
                        }`}
                        data-testid={`pulse-yesno-${idx}-val-${opt.val}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Submit */}
        <div className="mt-6 pb-8">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-teal-600 hover:bg-teal-700 py-3 text-base"
            data-testid="pulse-survey-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.submitting}</>
            ) : (
              t.submit
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PulseSurveyResponse;
